import React, { useState, useEffect } from 'react';
import { Tooltip, Alert, Table, Modal, Tabs, Descriptions, Typography, Tag, Card, Space, Timeline, Spin } from 'antd';
import { 
  CheckCircleOutlined, 
  ClockCircleOutlined, 
  ExclamationCircleOutlined, 
  InfoCircleOutlined,
  RobotOutlined,
  ApiOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  CodeOutlined,
  SearchOutlined,
  LinkOutlined,
  UserOutlined,
  MailOutlined,
  FileImageOutlined,
  GlobalOutlined,
  BranchesOutlined, 
} from '@ant-design/icons';
import axios from 'axios';
import config from '../config';

const { Text } = Typography;
const { TabPane } = Tabs;

const WorkflowDetailsModal = ({ execution, visible, onClose }) => {
  const [selectedStep, setSelectedStep] = useState(null);
  const [workflowData, setWorkflowData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [nodeNameMap, setNodeNameMap] = useState({});

  useEffect(() => {
    if (visible && execution && execution.workflow_id) {
      fetchWorkflowData(execution.workflow_id);
    }
  }, [visible, execution]);

  const fetchWorkflowData = async (workflowId) => {
    setLoading(true);
    console.log('Fetching workflow details for ID:', workflowId);
    
    try {
      console.log('Making API request to:', `${config.apiSettings.root}wp-ai-workflows/v1/workflows/${workflowId}`);
      
      const response = await axios.get(`${config.apiSettings.root}wp-ai-workflows/v1/workflows/${workflowId}`, {
        headers: {
          'X-WP-Nonce': config.apiSettings.nonce
        }
      });
      
      console.log('Workflow details API response:', response);
      console.log('Workflow data structure:', {
        hasNodes: !!response.data?.nodes,
        nodesCount: Array.isArray(response.data?.nodes) ? response.data.nodes.length : 'N/A',
        hasEdges: !!response.data?.edges,
        hasId: !!response.data?.id,
        hasName: !!response.data?.name,
        dataKeys: response.data ? Object.keys(response.data) : []
      });
      
      setWorkflowData(response.data);
      
      // Build a map of node IDs to their custom names from the workflow definition
      const nameMap = {};
      if (response.data && Array.isArray(response.data.nodes)) {
        console.log('Processing nodes for name mapping...');
        response.data.nodes.forEach(node => {
          console.log('Node data:', {
            id: node.id,
            type: node.type,
            hasData: !!node.data,
            hasNodeName: !!(node.data && node.data.nodeName)
          });
          
          if (node.id && node.data && node.data.nodeName) {
            nameMap[node.id] = node.data.nodeName;
          }
        });
        console.log('Generated node name map:', nameMap);
      } else {
        console.warn('No nodes array found or nodes is not an array');
      }
      setNodeNameMap(nameMap);
    } catch (error) {
      console.error('Error fetching workflow data:', error);
      console.error('Error details:', {
        message: error.message,
        responseStatus: error.response?.status,
        responseData: error.response?.data,
        requestConfig: error.config
      });
      
      // Try to log the raw error response if possible
      if (error.response) {
        try {
          console.error('Raw error response:', JSON.stringify(error.response.data));
        } catch (e) {
          console.error('Could not stringify error response data');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  if (!execution) return null;

  // Function to get node name from workflow definition or execution data
  const getNodeName = (nodeId) => {
    // First priority: Check our map of node names from the workflow definition
    if (nodeNameMap[nodeId]) {
      return nodeNameMap[nodeId];
    }
    
    // Second priority: Check if we have the node data in the execution's output_data
    if (execution.output_data) {
      const outputData = typeof execution.output_data === 'string' 
        ? JSON.parse(execution.output_data) 
        : execution.output_data;
      
      if (outputData && outputData[nodeId]) {
        const nodeData = outputData[nodeId];
        
        // Check for nodeName field
        if (nodeData.nodeName) {
          return nodeData.nodeName;
        }
        
        // Check in data if it exists
        if (nodeData.data && nodeData.data.nodeName) {
          return nodeData.data.nodeName;
        }
      }
    }
    
    // Third priority: Check the workflowData directly
    if (workflowData && Array.isArray(workflowData.nodes)) {
      const foundNode = workflowData.nodes.find(node => node.id === nodeId);
      if (foundNode && foundNode.data && foundNode.data.nodeName) {
        return foundNode.data.nodeName;
      }
    }
    
    // Last resort: Generate a readable name from the node ID
    const nodeTypeParts = nodeId.split('-');
    if (nodeTypeParts.length >= 2) {
      const nodeType = nodeTypeParts[0];
      const formattedType = nodeType.charAt(0).toUpperCase() + nodeType.slice(1);
      return `${formattedType} Node`;
    }
    
    return nodeId;
  };

  const transformNodeData = (execution) => {
    if (!execution) return [];
    
    // Define annotation node prefixes to exclude
    const annotationPrefixes = ['stickyNote-', 'textAnnotation-', 'shape-'];

    // Helper function to check if a node is an annotation
    const isAnnotationNode = (nodeId) => 
      annotationPrefixes.some(prefix => nodeId.startsWith(prefix));

    // If output_data is already an array of node summaries, use it directly
    if (Array.isArray(execution.node_summary) && execution.node_summary.length > 0) {
      // Filter out invalid nodes and annotation nodes
      return execution.node_summary.filter(node => 
        node && 
        node.node_id && 
        node.node_id.trim() !== '' && 
        node.node_id !== 'undefined' &&
        !isAnnotationNode(node.node_id)
      ).map(node => ({
        ...node,
        key: node.node_id,
        node_name: node.node_name || getNodeName(node.node_id)
      }));
    } 

    // Convert output_data object to node_summary array
    if (typeof execution.output_data === 'string') {
      try {
        execution.output_data = JSON.parse(execution.output_data);
      } catch (e) {
        return [];
      }
    }

    return Object.entries(execution.output_data || {})
      .filter(([nodeId]) => 
        nodeId && 
        nodeId.trim() !== '' && 
        nodeId !== 'undefined' &&
        !isAnnotationNode(nodeId)
      )
      .map(([nodeId, nodeData]) => {
        // Look specifically for the user-defined nodeName
        const nodeName = nodeData.nodeName || getNodeName(nodeId);
        
        return {
          key: nodeId,
          node_id: nodeId,
          node_name: nodeName,
          status: nodeData.type || 'Unknown',
          type: nodeData.type,
          message: nodeData.content || nodeData.message || 'No message'
        };
      });
  };

  const getNodeIcon = (type) => {
    const icons = {
      'trigger': <UserOutlined />,
      'aiModel': <RobotOutlined />,
      'sendEmail': <MailOutlined />,
      'unsplash': <FileImageOutlined />,
      'firecrawl': <GlobalOutlined />,
      'condition': <BranchesOutlined />,
      'humanInput': <UserOutlined />,
      'api': <ApiOutlined />,
      'database': <DatabaseOutlined />,
      'output': <FileTextOutlined />,
      'code': <CodeOutlined />,
      'research': <SearchOutlined />,
      'webhook': <LinkOutlined />,
      'error': <ExclamationCircleOutlined />,
    };
    return icons[type] || <InfoCircleOutlined />;
  };

  const getStatusColor = (status) => {
    const colors = {
      'completed': 'green',
      'processing': 'blue',
      'scheduled': 'orange',
      'error': 'red',
      'trigger': 'blue',
      'unknown': 'default'
    };
    return colors[status?.toLowerCase()] || 'default';
  };

  const formatData = (data) => {
    if (!data) return 'No data';
    if (typeof data === 'string') return data;
    try {
      return JSON.stringify(data, null, 2);
    } catch (e) {
      return 'Unable to display data';
    }
  };

  const formatInputData = (data) => {
    if (!data) return 'No input data';
    try {
      return typeof data === 'string' ? data : JSON.stringify(data, null, 2);
    } catch (e) {
      return 'Invalid input data format';
    }
  };

  const renderTimeline = () => {
    const nodes = transformNodeData(execution);
    
    return (
      <Timeline style={{ padding: '20px' }}>
        {nodes.map((node, index) => (
          <Timeline.Item
            key={node.key || index}
            dot={getNodeIcon(node.type)}
            color={getStatusColor(node.status)}
          >
            <Card
              size="small"
              style={{ 
                marginBottom: 16,
                backgroundColor: node.status === 'error' ? '#fff1f0' : '#f6ffed',
                borderColor: node.status === 'error' ? '#ffa39e' : '#b7eb8f'
              }}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <Space>
                  <Text strong>{getNodeName(node.node_id)}</Text>
                  <Text type="secondary" style={{ fontSize: '12px' }}>({node.node_id})</Text>
                  <Tag color={getStatusColor(node.status)}>
                    {node.status.toUpperCase()}
                  </Tag>
                </Space>
                
                <div style={{ 
                  backgroundColor: '#fff',
                  padding: '12px',
                  borderRadius: '6px',
                  maxHeight: '200px',
                  overflow: 'auto',
                  border: '1px solid #f0f0f0'
                }}>
                  <pre style={{ 
                    margin: 0, 
                    fontSize: '13px',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word'
                  }}>
                    {formatData(node.message)}
                  </pre>
                </div>
              </Space>
            </Card>
          </Timeline.Item>
        ))}
      </Timeline>
    );
  };

  // Process cost details to add custom user-defined node names
  const processCostDetails = () => {
    let costDetails = [];
    
    // Parse cost details
    if (Array.isArray(execution.cost_details)) {
      costDetails = execution.cost_details;
    } else if (typeof execution.cost_details === 'string') {
      try {
        costDetails = JSON.parse(execution.cost_details);
      } catch (e) {
        costDetails = [];
      }
    }
    
    // Map node_ids to their custom names using our nodeNameMap
    return costDetails.map(detail => ({
      ...detail,
      node_name: getNodeName(detail.node_id)
    }));
  };

  return (
    <Modal
      title={
        <Space>
          <Text strong>Workflow Execution Details</Text>
          <Tag color={getStatusColor(execution.status)}>
            {execution.status?.toUpperCase()}
          </Tag>
          {loading && <Spin size="small" />}
        </Space>
      }
      visible={visible}
      onCancel={onClose}
      width={1000}
      footer={null}
      bodyStyle={{ padding: '12px' }}
    >
      <Tabs defaultActiveKey="overview">
        <TabPane tab="Overview" key="overview">
          <Card className="mb-4">
            <Descriptions column={2} size="small">
              <Descriptions.Item label="Workflow Name" span={2}>
                <Text strong>{execution.workflow_name}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Execution ID">{execution.id}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={getStatusColor(execution.status)}>
                  {execution.status?.toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Total Cost" span={2}>
                <Space>
                  <Tag color="blue" style={{ fontSize: '16px', padding: '4px 8px' }}>
                    ${Number(execution.total_cost || 0).toFixed(4)}
                  </Tag>
                  <Tooltip title="Total cost of all AI operations in this execution">
                    <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                  </Tooltip>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Created At">{execution.created_at}</Descriptions.Item>
              <Descriptions.Item label="Updated At">{execution.updated_at}</Descriptions.Item>
            </Descriptions>
          </Card>

          <div style={{ 
            backgroundColor: '#fff', 
            borderRadius: '8px',
            maxHeight: '600px',
            overflow: 'auto'
          }}>
            {renderTimeline()}
          </div>
        </TabPane>

        <TabPane tab="Input Data" key="input">
          <Card bodyStyle={{ maxHeight: '600px', overflow: 'auto' }}>
            <pre style={{ 
              backgroundColor: '#f6f8fa',
              padding: '12px',
              borderRadius: '6px',
              fontSize: '13px',
              margin: 0,
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word'
            }}>
              {formatInputData(execution.input_data)}
            </pre>
          </Card>
        </TabPane>
        <TabPane 
          tab={
            <span>
              Cost Details
            </span>
          } 
          key="costs"
        >
          <Alert
            message="Cost Breakdown"
            description="Detailed cost information for each AI operation in this workflow execution."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Table
            dataSource={processCostDetails()}
            columns={[
              {
                title: 'Node',
                key: 'node',
                render: (_, record) => (
                  <Space direction="vertical" size={0}>
                    <Text strong>{record.node_name || getNodeName(record.node_id)}</Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>{record.node_id}</Text>
                  </Space>
                )
              },
              {
                title: 'Model',
                dataIndex: 'model',
                key: 'model',
                render: (model) => (
                  <Tag color="purple">{model}</Tag>
                )
              },
              {
                title: 'Provider',
                dataIndex: 'provider',
                key: 'provider',
                render: (provider) => (
                  <Tag color="cyan">{provider}</Tag>
                )
              },
              {
                title: 'Input Tokens',
                dataIndex: 'prompt_tokens',
                key: 'prompt_tokens',
                align: 'right',
                render: (tokens) => Number(tokens).toLocaleString()
              },
              {
                title: 'Output Tokens',
                dataIndex: 'completion_tokens',
                key: 'completion_tokens',
                align: 'right',
                render: (tokens) => Number(tokens).toLocaleString()
              },
              {
                title: 'Cost',
                dataIndex: 'cost',
                key: 'cost',
                align: 'right',
                render: (cost) => (
                  <Text strong>
                    ${Number(cost).toFixed(4)}
                  </Text>
                )
              }
            ]}
            pagination={false}
            size="small"
            summary={(pageData) => {
              if (!Array.isArray(pageData)) return null;
              
              const total = pageData.reduce((sum, row) => sum + Number(row.cost || 0), 0);
              const totalInputTokens = pageData.reduce((sum, row) => sum + Number(row.prompt_tokens || 0), 0);
              const totalOutputTokens = pageData.reduce((sum, row) => sum + Number(row.completion_tokens || 0), 0);
              
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={3}>
                      <strong>Totals</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      {totalInputTokens.toLocaleString()}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right">
                      {totalOutputTokens.toLocaleString()}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right">
                      <Tag color="blue" style={{ fontSize: '14px' }}>
                        ${total.toFixed(4)}
                      </Tag>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </TabPane>
      </Tabs>
    </Modal>
  );
};

export default WorkflowDetailsModal;